import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MdKeyboardArrowDown, MdDeleteOutline, MdAddCircle, MdSearch, MdRefresh, MdFileDownload, MdArrowForward } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiExternalLink, FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Utils,
  fetchWithAuthLogged,
  selectStyles,
  handleSessionExpired,
  getDefaultPageSize,
  fetchApplicationAppId,
  fetchAjaxListData,
  formatDateTime,
  can,
  exportListCsv,
  labelizeKey,
  dateRangeLabel,
} from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import "./engage.css";

const EMPTY_FILTERS = { name: "", event: "", mode: "" };

const MODE_OPTIONS = [
  { value: "", label: "All step orders" },
  { value: "ordered", label: "In order" },
  { value: "any", label: "Any order" },
];

export default function FunnelList() {
  const navigate = useNavigate();
  const { appId: routeAppId } = useParams();
  const [appUuid, setAppUuid] = useState(null);
  const loadApp = useCallback(async () => {
    const uuid = await fetchApplicationAppId(routeAppId);
    if (uuid) setAppUuid(uuid);
    return uuid;
  }, [routeAppId]);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } =
    useActionDropdown({
      tableScrollRef,
      optionCount: () => 1 + (can("funnel.delete") ? 1 : 0),
      menuWidth: 200,
      useAnchored: true,
    });

  const fetchEventOpts = useMemo(
    () =>
      ({ search, ids }) =>
        fetchAjaxListData({ type: "campaign-event", q: search, id: ids, extra: { app_id: appUuid, with_metrics: 1 }, mode: "options" }),
    [appUuid]
  );

  const loadData = useCallback(
    async (nextPage = page, nextFilters = filters, uuid = appUuid) => {
      if (!uuid) return;
      setLoading(true);
      const res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/funnels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          app_id: uuid,
          page: nextPage,
          pageSize,
          name: (nextFilters.name || "").trim(),
          event: nextFilters.event || "",
          mode: nextFilters.mode || "",
        }),
      });
      const result = await res.json().catch(() => ({}));
      setLoading(false);
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Failed to load funnels");
        return;
      }
      setRows(result.data || []);
      setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
    },
    [appUuid, page, filters, pageSize]
  );

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if (f.event) sp.set("event", f.event);
    if (f.mode) sp.set("mode", f.mode);
    setSearchParams(sp, { replace: true });
  };

  useEffect(() => {
    if (!routeAppId) return;
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const f = {
      name: searchParams.get("name") || "",
      event: searchParams.get("event") || "",
      mode: searchParams.get("mode") || "",
    };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(f.name || f.event || f.mode));
    (async () => {
      const uuid = (await loadApp()) || appUuid;
      if (uuid) loadData(p, f, uuid);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeAppId, searchParams]);

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    writeParams(1, f);
  };

  const onResetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, EMPTY_FILTERS);
  };

  const openFunnel = (row) => {
    if (!row?.id) return;
    navigate(`/apps/${routeAppId}/engage/funnel/${row.id}`, { state: { listSearch: searchParams.toString() } });
  };

  const openCreate = () => navigate(`/apps/${routeAppId}/engage/funnel/add`, { state: { listSearch: searchParams.toString() } });

  const deleteFunnel = async (row) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `"${row.name}" will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}delete/funnel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: row.id, app_id: appUuid }),
    });
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success(result.message || "Funnel deleted");
    const nextPage = rows.length === 1 && page > 1 ? page - 1 : page;
    if (nextPage !== page) writeParams(nextPage, filters);
    else loadData(page, filters);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv(
        "get/funnels",
        { app_id: appUuid, name: (filters.name || "").trim(), event: filters.event || "", mode: filters.mode || "" },
        "funnels.csv"
      );
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Funnel</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData(page, filters)}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("funnel.manage") ? (
              <button type="button" className="add_app_btn btn_theme_primary" onClick={openCreate}>
                <MdAddCircle />
                <span>Create funnel</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown
              className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
              size={22}
            />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <input
                      className="form-control"
                      placeholder="Funnel name"
                      value={draftFilters.name}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && onApplyFilters()}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <AsyncSearchSelect
                      inputId="funnel-filter-event"
                      instanceId="funnel-filter-event"
                      classNamePrefix="ns-funnel-filter-event"
                      placeholder="Contains event"
                      enabled={Boolean(appUuid)}
                      fetchOptions={fetchEventOpts}
                      value={draftFilters.event || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, event: opt?.value ?? "" }))}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <Select
                      inputId="funnel-filter-mode"
                      instanceId="funnel-filter-mode"
                      classNamePrefix="ns-funnel-filter-mode"
                      isSearchable={false}
                      options={MODE_OPTIONS}
                      value={MODE_OPTIONS.find((o) => o.value === draftFilters.mode) || MODE_OPTIONS[0]}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, mode: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" title="Apply filters" onClick={onApplyFilters}>
                        <MdSearch />
                        <span>Search</span>
                      </button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Reset filters" onClick={onResetFilters}>
                        <MdRefresh />
                        <span>Reset</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Funnel name</th>
                      <th>Steps</th>
                      <th>Step order</th>
                      <th>Complete within</th>
                      <th>Date range</th>
                      <th>Created by</th>
                      <th>Last updated</th>
                      <th className="col-action" style={{ textAlign: "center" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No funnels found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.id} className="campaign-perf-row--clickable" onClick={() => openFunnel(r)}>
                          <td>
                            <div style={{ lineHeight: 1.3 }}>
                              <span>{r.name}</span>
                              {r.description ? (
                                <div style={{ fontSize: "0.72em", color: "#999" }}>{r.description}</div>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div className="funnel-list-steps" title={(r.steps || []).map(labelizeKey).join(" → ")}>
                              {(r.steps || []).map((s, i) => (
                                <React.Fragment key={`${s}-${i}`}>
                                  {i > 0 && <MdArrowForward className="funnel-list-steps-arrow" />}
                                  <span className="engage-type-pill">{labelizeKey(s)}</span>
                                </React.Fragment>
                              ))}
                            </div>
                          </td>
                          <td>{r.mode === "any" ? "Any order" : "In order"}</td>
                          <td>{r.window_days ? `${r.window_days} ${r.window_days === 1 ? "day" : "days"}` : "No time limit"}</td>
                          <td>{dateRangeLabel(r.filters?.date_preset, r.filters?.date_from, r.filters?.date_to)}</td>
                          <td>{r.created_by_label || "—"}</td>
                          <td>{r.updatedAt ? formatDateTime(r.updatedAt) : "—"}</td>
                          <td className="col-action" style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                            <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                              <button
                                type="button"
                                className="dropdown-trigger-btn btn btn-link p-0"
                                aria-label="Actions"
                                aria-expanded={openActionId === r.id}
                                onClick={(e) => handleOpenDropdown(e, r.id, r)}
                              >
                                <BsThreeDotsVertical />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total}
                pageSize={pageSize}
                totalPages={meta.totalPages}
                ariaLabel="Funnel pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {openActionItem && dropdownPosition
          ? createPortal(
              <div
                ref={dropdownRef}
                style={{
                  position: "fixed",
                  left: dropdownPosition.left,
                  top: dropdownPosition.top,
                  zIndex: 999999,
                  minWidth: "200px",
                }}
              >
                <div className="dropdown-menu-subscription">
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => openFunnel(openActionItem))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <FiExternalLink size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>View</span>
                  </button>
                  {can("funnel.delete") ? (
                    <button
                      type="button"
                      className="delete-class"
                      onClick={() => handleDropdownAction(() => deleteFunnel(openActionItem))}
                      style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                    >
                      <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                      <span style={{ fontSize: "0.875rem" }}>Delete</span>
                    </button>
                  ) : null}
                </div>
              </div>,
              document.body
            )
          : null}
      </div>
    </div>
  );
}
